import { useCallback, useEffect, useState } from "react";
import { isEmbeddedInHub } from "../suiteSession";

export function isFacturxPwaStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return true;
  }
  const installedModes = [
    "standalone",
    "fullscreen",
    "minimal-ui",
    "window-controls-overlay",
  ] as const;
  return installedModes.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches);
}

export function isFacturxIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

type FacturxPwaBridge = {
  isStandalone: boolean;
  isIos: boolean;
  canNativeInstall: boolean;
  showInstallUi: boolean;
  promptInstall: () => Promise<boolean>;
};

function readFacturxPwaBridge(): FacturxPwaBridge {
  const standalone = isFacturxPwaStandalone();
  const embedded = isEmbeddedInHub();
  const bridge = window.facturxPwa;
  return {
    isStandalone: standalone,
    isIos: bridge?.isIos ?? isFacturxIos(),
    canNativeInstall: bridge?.canNativeInstall ?? false,
    showInstallUi: !standalone && !embedded,
    promptInstall: bridge?.promptInstall ?? (async () => false),
  };
}

export function useFacturxPwaInstall() {
  const [state, setState] = useState(readFacturxPwaBridge);

  useEffect(() => {
    const sync = () => setState(readFacturxPwaBridge());
    sync();
    window.addEventListener("facturx-pwa-sync", sync);
    window.addEventListener("beforeinstallprompt", sync);
    window.addEventListener("appinstalled", sync);
    const onDisplayMode = () => sync();
    window.matchMedia("(display-mode: standalone)").addEventListener("change", onDisplayMode);
    window.matchMedia("(display-mode: window-controls-overlay)").addEventListener("change", onDisplayMode);
    return () => {
      window.removeEventListener("facturx-pwa-sync", sync);
      window.removeEventListener("beforeinstallprompt", sync);
      window.removeEventListener("appinstalled", sync);
      window.matchMedia("(display-mode: standalone)").removeEventListener("change", onDisplayMode);
      window.matchMedia("(display-mode: window-controls-overlay)").removeEventListener("change", onDisplayMode);
    };
  }, []);

  const install = useCallback(async () => readFacturxPwaBridge().promptInstall(), []);

  return {
    isStandalone: state.isStandalone,
    canNativeInstall: state.canNativeInstall,
    isIos: state.isIos,
    showInstallUi: state.showInstallUi,
    install,
  };
}

const FX_PWA_INSTALL_DISMISS_KEY = "facturx_pwa_install_dismiss";

export function readSessionFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function writeSessionFlag(key: string): void {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

export { FX_PWA_INSTALL_DISMISS_KEY };
